import { useEffect, useRef } from 'react';
import './Lightbox.css';

export interface LightboxImage {
  src: string;
  alt: string;
  caption?: string;
}

interface LightboxProps {
  image: LightboxImage | null;
  onClose: () => void;
}

/**
 * Accessible image lightbox. Renders nothing when `image` is null.
 *
 * Behaviour:
 *  - Click the backdrop or the close button to dismiss.
 *  - Press Escape to dismiss.
 *  - Body scroll is locked while open.
 *  - Focus moves to the close button on open and is released when closed.
 */
export default function Lightbox({ image, onClose }: LightboxProps) {
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const lastFocusedRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!image) return;

    // Remember what was focused so we can restore focus on close.
    lastFocusedRef.current = document.activeElement as HTMLElement;

    // Lock body scroll
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Move focus into the dialog
    closeBtnRef.current?.focus();

    // Close on Esc
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', handleKey);
      lastFocusedRef.current?.focus?.();
    };
  }, [image, onClose]);

  if (!image) return null;

  return (
    <div
      className="lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Image viewer"
      onClick={onClose}
    >
      <button
        ref={closeBtnRef}
        type="button"
        className="lightbox__close"
        onClick={onClose}
        aria-label="Close image viewer"
      >
        <span aria-hidden="true">×</span>
      </button>
      <figure
        className="lightbox__figure"
        onClick={(e) => e.stopPropagation()}
      >
        <img src={image.src} alt={image.alt} className="lightbox__image" />
        {image.caption && (
          <figcaption className="lightbox__caption">{image.caption}</figcaption>
        )}
      </figure>
    </div>
  );
}
